/** The person fields a signed-in user edits on their own profile (docs/account.md). */
export const PROFILE_FIELDS = ['firstName', 'lastName', 'phone', 'birthDate', 'gender'] as const
export type ProfileField = (typeof PROFILE_FIELDS)[number]

const OWNER_REQUIRED: readonly ProfileField[] = ['firstName', 'lastName', 'birthDate']
const NONE_REQUIRED: readonly ProfileField[] = []

/**
 * Which profile fields the person may not leave empty. Every field is optional, except that a premise owner
 * keeps the names and the birth date that premise registration asks for: the birth date is needed for legal
 * reasons, the names identify who runs the premise. The API refuses to clear them and the form marks them
 * required, both from this one list.
 */
export function requiredProfileFields(user: { entityOwner: boolean }): readonly ProfileField[] {
  return user.entityOwner ? OWNER_REQUIRED : NONE_REQUIRED
}

/** What a profile edit sends for each field: `undefined` leaves it, `''` or `null` clears it. */
export type ProfilePatch = Partial<Record<ProfileField, string | null | undefined>>

/**
 * The first field `requiredProfileFields` keeps that `patch` would empty, or `null` when the edit may go ahead. A
 * field the patch does not send is left as it is, so it never counts - an owner registered before the birth date was
 * asked for can still save their name.
 */
export function clearedRequiredField(user: { entityOwner: boolean }, patch: ProfilePatch): ProfileField | null {
  for (const field of requiredProfileFields(user)) {
    if (!(field in patch) || patch[field] === undefined) continue
    const value = patch[field]
    if (value === null || value.trim() === '') return field
  }
  return null
}
